import type { AppliedSort, FundDatum } from "@/FundExplorer/types";
import type { DatumValue } from "hsl-core/FundExplorer";
import { API } from "hsl-core/FundExplorer";

export default function useSortedFunds(
	funds: FundDatum[],
	sortMap: AppliedSort,
) {
	const [sort] = Object.entries(sortMap);
	const [entityKey, order] = sort ?? [];
	if (!entityKey || !order) return funds;
	const key = `__${entityKey.split(".")[1]}` as keyof FundDatum;
	const hasValues = (val: DatumValue | DatumValue[]) => {
		if (!val) {
			return false;
		}
		if (typeof val === "object") {
			if (Array.isArray(val)) {
				return val.length > 0;
			}
			return Object.keys(val).length > 0;
		}
		if (typeof val === "string") {
			return val.trim().length > 0;
		}
		if (typeof val === "number") {
			return true;
		}
		return false;
	};
	const fundsWithoutValues = funds.filter(
		(fund) => !hasValues(fund[key] as DatumValue),
	);
	const fundsWithValues = funds.filter((fund) =>
		hasValues(fund[key] as DatumValue),
	);
	return fundsWithValues
		.sort(function sortFn(a, b) {
			const aVal = a[key as keyof FundDatum];
			const bVal = b[key as keyof FundDatum];
			if (typeof aVal === "string" && typeof bVal === "string") {
				return order === "asc"
					? aVal.localeCompare(bVal)
					: bVal.localeCompare(aVal);
			}
			if (typeof aVal === "number" && typeof bVal === "number") {
				return order === "asc" ? aVal - bVal : bVal - aVal;
			}
			if (Array.isArray(aVal) && Array.isArray(bVal)) {
				return sortFn(
					{ [key]: aVal[0] } as unknown as FundDatum,
					{ [key]: bVal[0] } as unknown as FundDatum,
				);
			}
			const maybeFundSizeA = API.FundSizeSchema.safeParse(aVal);
			const maybeFundSizeB = API.FundSizeSchema.safeParse(bVal);
			// fund size
			if (maybeFundSizeA.success && maybeFundSizeB.success) {
				const fundSizeA = Number.parseInt(`${maybeFundSizeA.data.GBP}`);
				const fundSizeB = Number.parseInt(`${maybeFundSizeB.data.GBP}`);
				return sortFn(
					{ [key]: fundSizeA } as unknown as FundDatum,
					{ [key]: fundSizeB } as unknown as FundDatum,
				);
			}
			return 0;
		})
		.concat(fundsWithoutValues);
}
